import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { NetSuiteAsset, BulkAssignUploadResults } from 'types'

export interface NetSuiteAssetManagerState {
  isLoading: boolean
  netSuiteAssets: { [key: number]: NetSuiteAsset }
  error: string | null
  searchCriteria: Record<string, string | number | boolean> | null
  bulkAssign: {
    file?: File
    fileName?: string
    uploadResults?: BulkAssignUploadResults
    isLoading: boolean
    error: string | null
  }
}

const initialState: NetSuiteAssetManagerState = {
  isLoading: false,
  netSuiteAssets: {},
  error: null,
  searchCriteria: null,
  bulkAssign: {
    file: null,
    fileName: '',
    uploadResults: null,
    isLoading: false,
    error: null,
  },
}

const slice = createSlice({
  name: 'NetSuiteAssetManager',
  initialState,
  reducers: {
    clearNetSuiteAssets(state) {
      state.netSuiteAssets = {}
      state.error = null
      state.isLoading = false
      // Note: searchCriteria is preserved, not cleared
    },
    fetchingNetSuiteAssets(state) {
      state.isLoading = true
      state.error = null
    },
    fetchingNetSuiteAssetsSuccess(
      state,
      action: PayloadAction<NetSuiteAsset[]>,
    ) {
      state.netSuiteAssets = Object.assign(
        {},
        state.netSuiteAssets,
        Object.fromEntries(
          action.payload?.map(asset => [asset.id, asset]) || [],
        ),
      )
      state.isLoading = false
      state.error = null
    },
    fetchingNetSuiteAssetsError(state, action: PayloadAction<string>) {
      state.isLoading = false
      state.error = action.payload
    },
    fetchingNetSuiteAssetsNotFound(state, action: PayloadAction<string>) {
      state.isLoading = false
      state.error = action.payload
    },
    setSearchQuery(
      state,
      action: PayloadAction<{
        key?: string
        value?: string | number | boolean | null
      }>,
    ) {
      if (
        action.payload === null ||
        (action.payload.key === undefined && action.payload.value === null)
      ) {
        // Clear all search criteria
        state.searchCriteria = null
      } else if (action.payload.key && action.payload.value !== undefined) {
        // Set specific key-value pair
        state.searchCriteria = {
          ...state.searchCriteria,
          [action.payload.key]: action.payload.value,
        }
      }
    },
    updatingNetSuiteAsset(state) {
      state.isLoading = true
      state.error = null
    },
    updatingNetSuiteAssetSuccess(state, action: PayloadAction<NetSuiteAsset>) {
      // Update the asset in the state with the new data
      if (action.payload && state.netSuiteAssets) {
        state.netSuiteAssets[action.payload.id] = action.payload
      }
      state.isLoading = false
      state.error = null
    },
    updatingNetSuiteAssetError(state, action: PayloadAction<string>) {
      state.isLoading = false
      state.error = action.payload
    },
    bulkAssignFileSubmitting(state, action: PayloadAction<File>) {
      state.bulkAssign.file = action.payload
      state.bulkAssign.fileName = action.payload.name
      state.bulkAssign.isLoading = true
      state.bulkAssign.error = null
      state.bulkAssign.uploadResults = null
    },
    bulkAssignFileSucceeded(
      state,
      action: PayloadAction<BulkAssignUploadResults>,
    ) {
      state.bulkAssign.isLoading = false
      state.bulkAssign.uploadResults = action.payload
      state.bulkAssign.error = action.payload.error
    },
    bulkAssignFileError(state, action: PayloadAction<string>) {
      state.bulkAssign.isLoading = false
      state.bulkAssign.error = action.payload
      state.bulkAssign.uploadResults = null
    },
  },
})

export const actions = slice.actions
export default slice.reducer
